import React, { useEffect, useState } from "react";
import PageHelmet from "../components/PageHelmet";
import {
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
  Alert,
  Modal,
} from "react-bootstrap";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  FiFileText,
  FiUsers,
  FiLayout,
  FiBriefcase,
} from "react-icons/fi";
import "../styles/Plans.css";
import api from "./PlanMock.js";
import CustomDataGrid from "../components/CustomDataGrid.jsx";
import { MdArrowBackIos } from "react-icons/md";
import {
  generateGridRows,
  generateGridColumns,
  existingPlansGridColumns,
  plansGridOptionalColumns,
  existingPlansRowMapping,
} from "../utils/PageUtils";

function Plans({ userRole, bearerToken }) {
  const [planFields, setPlanFields] = useState({
    name: "",
    description: "",
    monthly_price: "",
    annual_price: "",
    quota: {
      campaigns: "",
      contacts: " ",
      templates: " ",
      collaterals: " ",
      qrcodes: " ",
      trackables: " ",
      requests: " ",
    },
  });


  const [plans, setPlans] = useState([]);
  const [planError, setPlanError] = useState(null);
  const [createPlanInfo, setCreatePlanInfo] = useState(null);
  const [createPlanError, setCreatePlanError] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [planUpdatedInfo, setPlanUpdatedInfo] = useState(null);
  const [planUpdateError, setPlanUpdateError] = useState(null);

  const featureFields = [
    { name: "campaigns", label: "Campaigns", icon: <FiLayout /> },
    { name: "contacts", label: "Contacts", icon: <FiUsers /> },
    { name: "templates", label: "Templates", icon: <FiFileText /> },
    { name: "collaterals", label: "Collaterals", icon: <FiBriefcase /> },
    { name: "qrcodes", label: "QR Codes", icon: <FiFileText /> },      // new
    { name: "trackables", label: "Trackables", icon: <FiFileText /> }, // new
    { name: "requests", label: "Requests", icon: <FiFileText /> },     // new
  ];


  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/subscription-plans");
        const plansData = res.data.map((plan) => ({
          ...plan,
          monthly_price: Number(plan.monthly_price?.toString().replace(/[^0-9.-]+/g, "") ?? 0),
          annual_price: Number(plan.annual_price?.toString().replace(/[^0-9.-]+/g, "") ?? 0),
        }));
        setPlans(plansData);
      } catch (error) {
        setPlanError("Unable to fetch Plans");
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleInputChange = (e) => {
    setPlanError(null);
    setCreatePlanError(null);
    setCreatePlanInfo(null);

    const { name, value } = e.target;

    if (name in planFields.quota) {
      // store as string to allow empty input
      setPlanFields((prev) => ({
        ...prev,
        quota: {
          ...prev.quota,
          [name]: value,
        },
      }));
    } else {
      setPlanFields((prev) => ({
        ...prev,
        [name]: value, // store as string
      }));
    }
  };

  const resetPlanFields = () => {
    setPlanFields({
      name: "",
      description: "",
      monthly_price: "",
      annual_price: "",
      overage_price: "",
      quota: {
        campaigns: "",
        contacts: "",
        templates: "",
        collaterals: "",
        qrcodes: "",
        trackables: "",
        requests: "",
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Plan fields", planFields)
    try {
      const formattedPlan = {
        ...planFields,
        monthly_price: Number(planFields.monthly_price || 0),
        annual_price: Number(planFields.annual_price || 0),
        overage_price: Number(planFields.overage_price || 0),
        quota: Object.fromEntries(
          Object.entries(planFields.quota).map(([k, v]) => [k, Number(v || 0)])
        ),
        plan_id: crypto.randomUUID(),
        created: new Date().toISOString(),
      };
      const res = await api.post("/subscription-plans", formattedPlan);
      console.log("Plan created:", res.data);
      if (res.status == 201) {
        setPlans([...plans, res.data]);
        setCreatePlanInfo("Plan created successfully!");
        resetPlanFields();
        setTimeout(() => {
          setShowCreateModal(false)
        }, 3000);
      } else {
        setCreatePlanError("Somwthing went wrong while creating a plan")
      }
    } catch (error) {
      setCreatePlanError("Something went wrong while creating a plan.");
    }
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    resetPlanFields();
    setCreatePlanError(null);
    setCreatePlanInfo(null);

  }
  const handleCloseCreateModal = () => setShowCreateModal(false);
  const handleClosePlanDetails = () => {
    setSelectedPlan(null);
    setSelectedRowId(null);
  }
  const handleRowClick = (e) => {
    setPlanError(null);
    setCreatePlanError(null);
    setCreatePlanInfo(null);
    if (selectedRowId === e.row.id) {
      setSelectedRowId(null);
      setSelectedPlan(null);
      return;
    }
    setSelectedRowId(e.row.id);
    const plan = plans.find((plan) => plan.plan_id === e.row.plan_id);
    console.log("Selected Plan:", plan);
    setSelectedPlan(plan);

  };

  useEffect(() => {
    // keep body scroll locked when details are open on small screens
    if (selectedPlan && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedPlan, isMobile]);

  // When edit modal opens, keep body locked (no layout shift) and allow modal body to scroll via CSS
  useEffect(() => {
    // no-op for body overflow here; modal body is scrollable via CSS rules
    // when modal closes, re-lock body if details panel is open on mobile
    if (!showEditModal) {
      if (selectedPlan && isMobile) document.body.style.overflow = "hidden";
    }
  }, [showEditModal, selectedPlan, isMobile]);

  // ensure create modal appears above full-screen panel; keep body locked to prevent layout shift
  useEffect(() => {
    if (showCreateModal) {
      // bump z-index in case full-screen panel is above
      const el = document.querySelector('.modal');
      if (el) el.style.zIndex = 12000;
    } else {
      if (selectedPlan && isMobile) document.body.style.overflow = "hidden";
    }
  }, [showCreateModal, selectedPlan, isMobile]);


  const handleEditPlan = () => {
    if (!selectedPlan) {
      setPlanError("Please select a plan to edit.");
      return;
    }
    setCreatePlanError(null);
    setCreatePlanInfo(null);
    setPlanUpdateError(null);
    setPlanUpdatedInfo(null);
    setPlanFields({
      name: selectedPlan.name || "",
      description: selectedPlan.description || "",
      monthly_price: selectedPlan.monthly_price ?? 0,
      annual_price: selectedPlan.annual_price ?? 0,
      overage_price: selectedPlan.overage_price ?? 0,
      quota: {
        campaigns: selectedPlan.quota?.campaigns ?? 0,
        contacts: selectedPlan.quota?.contacts ?? 0,
        templates: selectedPlan.quota?.templates ?? 0,
        collaterals: selectedPlan.quota?.collaterals ?? 0,
        qrcodes: selectedPlan.quota?.qrcodes ?? 0,
        trackables: selectedPlan.quota?.trackables ?? 0,
        requests: selectedPlan.quota?.requests ?? 0,
      },
    });

    console.log("Plan fields", planFields)
    console.log("Selected plan", selectedPlan)
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    console.log("Editing Plan with fields:", planFields);
    const plan_id = selectedPlan.plan_id;

    try {
      const updatedPlans = plans.map((plan) =>
        plan.plan_id === selectedPlan.plan_id ? planFields : plan
      );

      const res = await api.patch(`/subscription-plans/${plan_id}`, planFields);
      console.log("Plan updated:", res.data);
      if (res.status == 200) {
        setPlanUpdatedInfo("Plan updated successfully!");
        setTimeout(() => {
          setShowEditModal(false)
        }, 3000);
        setPlans(updatedPlans);
        setSelectedPlan(planFields)
      } else {
        setPlanUpdateError("Error updating plan.");
      }
    } catch (error) {
      setCreatePlanError("Error updating plan.");
    }
  };

  // Format prices for display in the grid
  const plansForGrid = plans.map((p) => ({
    ...p,
    quota:
      typeof p.quota === "object" && p.quota !== null
        ? Object.entries(p.quota)
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n")
        : p.quota,
  }));

  const existingPlansRows = generateGridRows(plansForGrid, existingPlansRowMapping);

  const existingPlansColumns = generateGridColumns(
    existingPlansGridColumns,
    "name",
    undefined,
    plansGridOptionalColumns
  );

  if (userRole !== "admin") {
    return (
      <div className="user-error">
        <h1>404 - Page Not Found</h1>
        <p>The page you're looking for doesn't exist.</p>
      </div>
    );
  }

  const renderPlanDetails = (showCloseIcon = true) => (
    <div className="plan-details-container">
      <div className="plan-details-header">
        <Button
          className="edit-btn"
          onClick={() => handleEditPlan(selectedPlan)}
        >
          Edit Plan
        </Button>

        {showCloseIcon && <IconButton onClick={handleClosePlanDetails} style={{ paddingRight: 0 }}>
          <CloseIcon />
        </IconButton>
        }

      </div>

      <p className="plan-details-heading"> PLAN DETAILS</p>

      <div className="plan-content">
        <div className="plan-fields">
          <p className="plan-field-label">ID:</p>
          <p className="plan-field-value">{selectedPlan.plan_id}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Name:</p>
          <p className="plan-field-value">{selectedPlan.name}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Description:</p>
          <p className="plan-field-value">{selectedPlan.description}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Monthly Price:</p>
          <p className="plan-field-value">${selectedPlan.monthly_price}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Annual Price:</p>
          <p className="plan-field-value">${selectedPlan.annual_price}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Created:</p>
          <p className="plan-field-value">{selectedPlan.created}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Updated:</p>
          <p className="plan-field-value">{selectedPlan.updated}</p>
        </div>
        <div className="plan-fields">
          <p className="plan-field-label">Quota:</p>
          {/* <p>{Object.entries(selectedPlan.quota).map(([k,v]) =>`${k}: ${v}`).join("\n")}</p> */}
          <div style={{ textAlign: "left" }} className="plan-field-value">
            {selectedPlan?.quota &&
              Object.entries(selectedPlan.quota).map(([k, v]) => (
                <div key={k}>{`${k}: ${v}`}</div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
  return (
    <div>
      <PageHelmet
        title="Plans"
        description="Create and view plans"
        url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/plans`}
      />

      <div className="plans-page">
        <div className="left-section">
          <h2 className="existing-plans-title">Subscription Plans</h2>
          <div className="plans-header">
            <Button
              className="new-btn"
              onClick={handleOpenCreateModal}
            >
              + New Plan
            </Button>
          </div>

          <div className="existing-plans-container">
            <CustomDataGrid
              rows={existingPlansRows}
              columns={existingPlansColumns}
              totalRows={existingPlansRows.length}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
              getRowHeight={() => "auto"}
            />
            {planError && (
              <Alert variant="danger" className="mt-3 text-center">
                {planError}
              </Alert>
            )}
          </div>
        </div>

        <div className={`plans-right-section ${isMobile && selectedPlan ? 'mobile-open' : ''}`}>
          {!selectedPlan ? (
            <div className="right-section-content">
              <p className="info-heading">Select a Plan</p>
              <p className="info-text">
                Click any plan row to view or manage details.
              </p>
            </div>
          ) : (
            !isMobile && (renderPlanDetails(true))
          )}
          {/* <div className="plan-details-container"> */}
          {isMobile && selectedPlan && (
            <div className="plan-mobile-screen mobile-open">
              <div className="mobile-header">
                <IconButton size="small" style={{ color: '#fff' }} onClick={handleClosePlanDetails}>
                  <MdArrowBackIos />
                </IconButton>
                <div className="mobile-title">{selectedPlan?.name || "Plan Details"}</div>
              </div>

              <div className="mobile-body">
                {renderPlanDetails(false)}
              </div>

            </div>

            // </div>
          )}
        </div>
      </div>


      <Modal
        show={showCreateModal}
        onHide={handleCloseCreateModal}
        centered
        backdrop="static"
        size="lg"
        dialogClassName="edit-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Plan</Modal.Title>
        </Modal.Header>
        <div className="divider"></div>

        <Modal.Body className="create-plan-card">

          {createPlanInfo && (
            <Alert variant="success" className="mt-3 text-center">
              {createPlanInfo}
            </Alert>
          )}
          {createPlanError && (
            <Alert variant="danger" className="mt-3 text-center">
              {createPlanError}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <div className="input-row">
              <div className="input-box blue-border">
                <div className="label-container">
                  <label>
                    Plan Name <span className="required">*</span>
                  </label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Plan name should be unique</Tooltip>}
                  >
                    <IconButton>
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </OverlayTrigger>
                </div>
                <Form.Control
                  type="text"
                  name="name"
                  value={planFields.name}
                  onChange={handleInputChange}
                  placeholder="Enter plan name"
                  required
                  pattern="[A-Za-z0-9\s]+"
                  title="Plan name should contain only alphabets"
                />
              </div>
              <div className="input-box blue-border">
                <div className="label-container">
                  <label>
                    Description <span className="required">*</span>
                  </label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Plan description</Tooltip>}
                  >
                    <IconButton>
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </OverlayTrigger>
                </div>
                <Form.Control
                  type="text"
                  name="description"
                  value={planFields.description}
                  onChange={handleInputChange}
                  placeholder="Enter plan description"
                  required
                  pattern="[A-Za-z0-9\s]+"
                  title="Plan description should contain only alphabets"
                />
              </div>
            </div>

            <div className="input-row">
              <div className="input-box purple-border">
                <div className="label-container">
                  <label>
                    Monthly Price <span className="required">*</span>
                  </label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Enter monthly price for the plan</Tooltip>}
                  >
                    <IconButton>
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </OverlayTrigger>

                </div>
                <Form.Control
                  type="number"
                  name="monthly_price"
                  value={planFields.monthly_price}
                  onChange={handleInputChange}
                  placeholder="Enter Monthly price for the plan"
                  required
                  min={0}
                />
              </div>
              <div className="input-box purple-border">
                <div className="label-container">
                  <label>
                    Annual Price <span className="required">*</span>
                  </label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Enter Annual price for the plan</Tooltip>}
                  >
                    <IconButton>
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </OverlayTrigger>

                </div>
                <Form.Control
                  type="number"
                  name="annual_price"
                  value={planFields.annual_price}
                  onChange={handleInputChange}
                  placeholder="Enter Annual price for the plan"
                  required
                  min={0}
                />
              </div>
            </div>

            <div className="input-box purple-border">
              <div className="label-container">
                <label>
                  Overage Charge per Mailer <span className="required">*</span>
                </label>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Enter overage charges.</Tooltip>}
                >
                  <IconButton>
                    <HelpOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </OverlayTrigger>
              </div>

              <Form.Control
                type="number"
                name="overage_price"
                value={planFields.overage_price}
                onChange={handleInputChange}
                placeholder="Enter overage charge"
                required
                min={0}
              />
            </div>

            <div className="features-box green-border">
              <h3>Quota</h3>
              <div className="features-grid">
                {featureFields.map((feature) => (
                  <div key={feature.name} className="feature-item">
                    <div className="feature-label">
                      {feature.icon}
                      {feature.label}
                    </div>
                    <Form.Control
                      type="number"
                      name={feature.name}
                      value={planFields.quota[feature.name] || ""}
                      placeholder="0"
                      onChange={handleInputChange}
                      className="feature-input"
                      required
                    />
                  </div>
                ))}
              </div>

            </div>

            <div className="button-container">
              <Button type="submit" className="create-btn">
                Create Plan
              </Button>
            </div>

          </Form>
        </Modal.Body>
      </Modal>


      {/* ✅ Edit Plan Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
        dialogClassName="edit-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Plan</Modal.Title>
        </Modal.Header>
        <div className="divider"></div>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <div className="input-row">
              <div className="input-box blue-border">
                <label className="modal-labels">Plan Name
                  <span className="required">
                    *
                  </span>
                </label>
                <Form.Control
                  type="text"
                  name="name"
                  value={planFields.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="input-box blue-border">
                <label className="modal-labels">Description
                  <span className="required">
                    *
                  </span>
                </label>
                <Form.Control
                  type="text"
                  name="description"
                  value={planFields.description}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="input-row">
              <div className="input-box purple-border">
                <label className="modal-labels">Monthly Price
                  <span className="required">
                    *
                  </span>
                </label>
                <Form.Control
                  type="number"
                  name="monthly_price"
                  value={planFields.monthly_price}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="input-box purple-border">
                <label className="modal-labels">Annual Price
                  <span className="required">
                    *
                  </span>
                </label>
                <Form.Control
                  type="number"
                  name="annual_price"
                  value={planFields.annual_price}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="input-box purple-border">
              <label className="modal-labels">Overage Charge per Mailer
                <span className="required">
                  *
                </span>
              </label>
              <Form.Control
                type="number"
                name="overage_price"
                value={planFields.overage_price}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="features-box green-border">
              <h3>Quota</h3>
              <div className="features-grid">
                {featureFields.map((feature) => (
                  <div key={feature.name} className="feature-item">
                    <div className="feature-label">
                      {feature.icon}
                      {feature.label}
                    </div>
                    <Form.Control
                      type="number"
                      name={feature.name}
                      value={planFields.quota[feature.name] || ""}
                      placeholder="0"
                      onChange={handleInputChange}
                      className="feature-input"
                      required
                      min={0}
                    />
                  </div>
                ))}
              </div>

            </div>

            <div className="button-container">
              <Button type="submit" className="create-btn">
                Save Changes
              </Button>
            </div>
          </Form>
        </Modal.Body>
        {planUpdatedInfo && (
          <Alert variant="success" className="mt-3 text-center">
            {planUpdatedInfo}
          </Alert>
        )}
        {planUpdateError && (
          <Alert variant="danger" className="mt-3 text-center">
            {planUpdateError}
          </Alert>
        )}
      </Modal>
    </div>
  );
}

export default Plans;
